import {
  Autocomplete,
  Avatar,
  Box,
  CircularProgress,
  TextField,
} from "@mui/material";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { IJiraUserDetails } from "../../common/interface";
import { getJiraUser, updateTaskDetails } from "../../services/jira.service";
import { customPopper } from "../CustomPopper";
import "./style.scss";

interface IJiraAssigneeSelectorProps {
  assignee: IJiraUserDetails;
  issueId: string;
  onAssigneeUpdated: () => void;
  label?: string;
  containerRef: React.RefObject<HTMLDivElement>;
}

const unassigned = {
  displayName: "Unassigned",
  accountId: null,
  avatarUrls: {
    "48x48": undefined,
  },
  emailAddress: "",
};

const JiraAssigneeSelector: React.FC<IJiraAssigneeSelectorProps> = ({
  assignee,
  issueId,
  onAssigneeUpdated,
  label,
  containerRef,
}) => {
  const [users, setUsers] = useState<IJiraUserDetails[]>([]); // Current users to show in dropdown
  const [initialUsers, setInitialUsers] = useState<IJiraUserDetails[]>([]); // Store initial users
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedAssignee, setSelectedAssignee] =
    useState<IJiraUserDetails | null>(assignee);

  const getUsers = useCallback(
    async (searchString?: string) => {
      setLoading(true);
      const { data: fetchedUsers } =
        (await getJiraUser({ text: searchString })) ?? {};
      if (searchString) {
        setUsers(fetchedUsers || []);
      } else {
        setUsers(fetchedUsers?.length ? fetchedUsers : initialUsers);
      }
      setLoading(false);
    },
    [initialUsers]
  );

  const debouncedUserSearch = debounce(getUsers, 500);

  // Fetch the users on component mount and store initial users
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      const { data: fetchedUsers } = (await getJiraUser({ text: "" })) ?? {};
      const usersToDisplay = fetchedUsers?.length ? fetchedUsers : [assignee];
      if (usersToDisplay?.length) {
        setInitialUsers(usersToDisplay);
        setUsers(usersToDisplay);
      }
      setLoading(false);
    };
    fetchUsers();
  }, [assignee]);

  // Update assignee in JIRA
  const updateAssignee = async (newAssignee: IJiraUserDetails) => {
    if (newAssignee?.accountId === selectedAssignee?.accountId) {
      return;
    }
    setLoading(true);
    try {
      const response = await updateTaskDetails({
        id: issueId,
        fields: {
          assignee: {
            accountId: newAssignee?.accountId,
          },
        },
      });
      if (response.statusCode === 200) {
        setSelectedAssignee(newAssignee);
        onAssigneeUpdated();
        toast.success("Assignee updates successfully");
      } else {
        toast.error("Failed to update assignee");
      }
    } catch {
      toast.error("Error updating assignee");
    } finally {
      setLoading(false);
    }
  };
  const userOptions = [unassigned, ...users];
  const popperComponent = customPopper(containerRef);
  return (
    <Box className="assignee-selector">
      {label && <p className="label-text">{label}</p>}
      <Autocomplete
        sx={{
          "& .MuiInputBase-root": {
            padding: "0px",
            paddingLeft: "3px",
          },
        }}
        PopperComponent={popperComponent}
        value={selectedAssignee ?? unassigned}
        onChange={(_, newValue) => {
          if (newValue) {
            updateAssignee(newValue as unknown as IJiraUserDetails);
          }
        }}
        onBlur={() => {
          setUsers(initialUsers);
        }}
        options={userOptions}
        getOptionLabel={(option) => option?.displayName || ""}
        disableClearable
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            fullWidth
            onChange={(event) => {
              if (event.target.value) {
                debouncedUserSearch(event.target.value);
              } else {
                getUsers();
              }
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) =>
          option ? (
            <Box
              component="li"
              {...props}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Avatar
                src={option?.avatarUrls?.["48x48"]}
                alt={option?.displayName}
                sx={{ mr: 1, height: 32, width: 32 }}
              />
              <Box>
                <strong className="label-text">{option?.displayName}</strong>
                <div>{option?.emailAddress}</div>
              </Box>
            </Box>
          ) : undefined
        }
        isOptionEqualToValue={(option, value) =>
          option?.accountId === value?.accountId
        }
      />
    </Box>
  );
};

export default JiraAssigneeSelector;
